<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditAperio.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditAperio.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditAperio.title') }}</li>
                    </ol>
                </div>
            </div>

            <form ref="formRef" id="aperioForm" name="aperioForm" novalidate @submit.prevent class="pb-4">
                <div class="row">
                    <div class="col-xxl-8 col-xl-10">
                        <div class="card">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('componentEditAperio.section') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="aperio-label" class="form-label">{{ $t('componentEditAperio.readerName') }}</label>
                                        <input id="aperio-label" type="text" class="form-control" v-model="form.label" maxlength="32"
                                            :placeholder="$t('componentEditAperio.readerNamePlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="aperio-door-label" class="form-label">{{ $t('componentEditAperio.doorName') }}</label>
                                        <input id="aperio-door-label" type="text" class="form-control" v-model="form.door_label" maxlength="32"
                                            :placeholder="$t('componentEditAperio.doorNamePlaceholder')" autocomplete="off" required />
                                    </div>
                                    <div class="mb-2 col-12">
                                        <label for="aperio-hub" class="form-label">{{ $t('componentEditAperio.hub') }}</label>
                                        <div class="input-group">
                                            <select id="aperio-hub" class="form-select" v-model="selectedIp" @change="resetLocks">
                                                <option value="" disabled>{{ $t('componentEditAperio.selectHub') }}</option>
                                                <option v-for="gateway in gateways" :key="gateway.ip" :value="gateway.ip">{{ gateway.name }} ({{ gateway.ip }})</option>
                                            </select>
                                            <button id="aperio-add-hub" type="button" class="btn btn-outline-primary" :title="$t('componentEditAperio.addHub')" :aria-label="$t('componentEditAperio.addHub')" @click="addHub">
                                                <i class="mdi mdi-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div class="text-center mt-2">
                                    <button id="aperio-discover" type="button" class="btn btn-outline-primary" :disabled="discovering || declaring" @click="discover">
                                        <span v-if="discovering" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                        <i v-else class="mdi mdi-magnify me-1"></i>{{ $t('componentEditAperio.discover') }}
                                    </button>
                                </div>

                                <!-- Serrures annoncées par le hub -->
                                <div v-if="locks !== null" id="aperio-locks" class="mt-3">
                                    <h5 class="text-muted text-uppercase">{{ locks.length === 0 ? $t('componentEditAperio.noLockFound') : $t('componentEditAperio.locks') }}</h5>
                                    <div v-for="(lock, index) in locks" :key="lock.device_group_id" class="form-check mb-2">
                                        <input :id="`aperio-found-lock-${index}`" class="form-check-input" type="radio" name="aperio-found-lock" :value="index"
                                            v-model="selectedIndex" :disabled="isDeclared(lock)" />
                                        <label class="form-check-label" :for="`aperio-found-lock-${index}`">
                                            <span class="fw-bold" :class="isDeclared(lock) ? 'text-muted' : 'text-primary'">{{ lockLabel(lock) }}</span>
                                            <span v-if="isDeclared(lock)" class="d-block font-13 text-muted">{{ $t('componentEditAperio.alreadyDeclared') }}</span>
                                            <span v-else-if="lock.mac" class="d-block font-13 text-muted">{{ lock.mac }}</span>
                                        </label>
                                    </div>
                                    <div v-if="locks.length > 0" class="text-center mt-2">
                                        <button id="aperio-declare" type="button" class="btn btn-primary" :disabled="declaring || discovering" @click="declare">
                                            <span v-if="declaring" class="spinner-border spinner-border-sm me-1" role="status"></span>{{ $t('componentEditAperio.declare') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
                </div>
            </form>
        </div>

        <GatewayModal ref="gatewayModal" />
    </div>
</template>

<script>
import { computed, nextTick, reactive, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';
import GatewayModal from '@/modals/gatewayModal.vue';

// Aperio integration of a LINK: discovers the locks of an Aperio hub and declares one as a reader of the LINK
export default {
    name: 'ComponentEditAperio',
    components: {
        GatewayModal,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const formRef = ref(null);
        const gatewayModal = ref(null);
        const discovering = ref(false);
        const declaring = ref(false);
        const form = reactive({ label: '', door_label: '' });
        const declaredLocks = hub.getDeclaredAperioLocks();

        // Hubs of the locks already declared, the first one selected as in the app
        const gateways = ref(hub.getMecaGateways('Aperio'));
        const selectedIp = ref(gateways.value.length > 0 ? gateways.value[0].ip : '');
        const selectedGateway = computed(() => gateways.value.find(g => g.ip === selectedIp.value));
        const locks = ref(null);
        const selectedIndex = ref(null);

        const pathToInstallationEditComponents = computed(() => `/installation-edit?id=${facilityId}&action=components`);

        const isDeclared = (lock) => declaredLocks.has(lock.device_group_id) || (lock.mac != null && declaredLocks.has(lock.mac));

        const lockLabel = (lock) => lock.name ? `${lock.name} (${lock.device_group_id})` : lock.device_group_id;

        // The discovered locks belong to the previous hub
        const resetLocks = () => {
            locks.value = null;
            selectedIndex.value = null;
        };

        const addHub = async () => {
            const gateway = await gatewayModal.value.show(t('componentEditAperio.addHub'));
            if (!gateway) return;
            if (!gateways.value.some(g => g.ip === gateway.ip)) gateways.value.push(gateway);
            selectedIp.value = gateway.ip;
            resetLocks();
        };

        const discover = async () => {
            if (!selectedGateway.value) {
                showToast('error', t('componentEditAperio.selectHub'));
                return;
            }
            discovering.value = true;
            resetLocks();
            try {
                locks.value = await userStore.discoverAperioLocks(facilityId, hub, selectedGateway.value.ip);
            } catch (message) {
                showToast('error', message);
            } finally {
                discovering.value = false;
            }
        };

        const declare = async () => {
            const lock = selectedIndex.value !== null ? locks.value[selectedIndex.value] : null;
            if (!lock) {
                showToast('error', t('componentEditAperio.selectLock'));
                return;
            }
            // The lock name is proposed when the installer has not typed one
            const name = lock.name.trim();
            if (form.label.trim() === '' && name !== '') form.label = name.substring(0, 32);
            form.label = form.label.trim();
            form.door_label = form.door_label.trim();
            await nextTick();
            if (!formRef.value.checkValidity()) {
                formRef.value.reportValidity();
                return;
            }

            declaring.value = true;
            try {
                await userStore.declareAperioLock(facilityId, hub, {
                    gateway_ip: selectedGateway.value.ip,
                    gateway_name: selectedGateway.value.name,
                    locks: [{ label: form.label, door_label: form.door_label, device_group_id: lock.device_group_id, mac: lock.mac }],
                });
                // The components list reloads the LINK with its new reader
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('componentEditAperio.lockAdded'));
            } catch (message) {
                showToast('error', message);
            } finally {
                declaring.value = false;
            }
        };

        return {
            formRef,
            gatewayModal,
            discovering,
            declaring,
            form,
            gateways,
            selectedIp,
            locks,
            selectedIndex,
            isDeclared,
            lockLabel,
            resetLocks,
            addHub,
            discover,
            declare,
            pathToInstallationEditComponents,
        };
    },
};
</script>
